import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    Label,
} from '@repo/shadcn';
import { useDebounce } from '@uidotdev/usehooks';
import { FC, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { PiPlusCircleDuotone } from 'react-icons/pi';

import { AutoCompleteInput } from '../../../../components/ui/autocomplete/auto-complete-input';
import { MODAL_ROUTE } from '../../../../constants';
import { useGetUnitsQuery } from '../../../../features/slice/products/unit/unit-api-slice';
import { useModal } from '../../../../hooks';
import { TProductForm } from './product-form.component';

export const UnitSelector: FC = () => {
    const [searchInputText, setSearchInputText] = useState('');
    const { openModal } = useModal();

    const { setValue, watch, control } = useFormContext<TProductForm>();

    const selectedUnitId = watch('unit_id');
    const selectedUnitName = watch('unit_name');

    const debouncedSearchInputText = useDebounce(searchInputText, 500);

    const { data, isLoading, isFetching } = useGetUnitsQuery({
        search: debouncedSearchInputText,
    });

    const handleInputChange = (value: string) => {
        setSearchInputText(value);
    };

    const handleValueChange = (value: any) => {
        if (!value) {
            setValue('unit_id', '');
            setValue('unit_name', '');
            return;
        }

        setValue('unit_id', value.value);
        setValue('unit_name', value.label);
    };

    const generateOptions = () => {
        const options =
            data?.data.map((unit: any) => ({
                label: unit.name,
                value: unit.id,
            })) || [];

        // add selected option if not in the list
        if (
            selectedUnitId &&
            selectedUnitName &&
            !options.find((opt: any) => opt.value === selectedUnitId)
        ) {
            options.unshift({
                label: selectedUnitName,
                value: selectedUnitId,
            });
        }

        return options;
    };

    const options = generateOptions();

    const selectedOption = {
        label: selectedUnitName || '',
        value: selectedUnitId || '',
    };

    return (
        <FormField
            control={control}
            name={'unit_id'}
            render={() => (
                <FormItem>
                    <FormLabel className="flex items-center justify-between">
                        <div>
                            <span>Unit</span>
                            <span className="ml-1 text-red-500">*</span>
                        </div>

                        <Label
                            onClick={() =>
                                openModal(MODAL_ROUTE.CREATE_PRODUCT_UNIT)
                            }
                            className="flex cursor-pointer items-center gap-1 text-primary hover:underline"
                        >
                            <PiPlusCircleDuotone size={16} className="-mt-1" />
                            Add Unit
                        </Label>
                    </FormLabel>

                    <FormControl>
                        <AutoCompleteInput
                            isLoading={isLoading || isFetching}
                            options={options}
                            value={selectedOption}
                            emptyMessage={'No items found'}
                            onValueChange={handleValueChange}
                            onInputChange={handleInputChange}
                            showSuggestionsOnFocus={true}
                        />
                    </FormControl>
                    <FormMessage className="line-clamp-1 text-xs" />
                </FormItem>
            )}
        />
    );
};

UnitSelector.displayName = 'UnitSelector';
